import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux'; 
import env from '../utils/env';
import { attendanceAPI } from '../services/api';
import AttendanceIcon from '../img/attendance.gif';
import axios from 'axios';
import { Modal, Button, Dropdown, OverlayTrigger, Popover } from 'react-bootstrap';
import { Search, CheckCircleFill, ExclamationTriangleFill } from 'react-bootstrap-icons';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import AttendanceGuideModal from '../Components/AttendanceGuideModal.jsx';
import StudentAttendance from '../Components/StudentAttendance.jsx';
import TeacherQRGenerator from './TeacherQRGenerator.jsx';
import '../CSS/Attendance.css';

function Attendance() {
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [date, setDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: '',
    variant: 'success'
  });
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [showRecords, setShowRecords] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [sortCriteria, setSortCriteria] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [recordsSearchQuery, setRecordsSearchQuery] = useState('');
  const [enrollmentStatus, setEnrollmentStatus] = useState({});
  const [currentStudentsPage, setCurrentStudentsPage] = useState(1);
  const [studentsPerPage] = useState(75);
  const [totalStudents, setTotalStudents] = useState(0);
  const [currentRecordsPage, setCurrentRecordsPage] = useState(1);
  const [recordsPerPage] = useState(75);
  const [totalRecords, setTotalRecords] = useState(0);
  const [userRole, setUserRole] = useState('');
  const user = useSelector(state => state.auth.user);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
const [isLoadingRecords, setIsLoadingRecords] = useState(false);

  useEffect(() => {
    setUserRole(user?.role || '');
  }, [user]);

  useEffect(() => {
    const fetchStudents = async () => {
        setIsLoadingStudents(true);
      try {
        const response = await attendanceAPI.getStudents({
          page: currentStudentsPage,
          limit: studentsPerPage,
          search: searchQuery,
          sortBy: sortCriteria,
          sortOrder: sortOrder
        });
        setStudents(response.data.data);
        setTotalStudents(response.data.total);
        const initialEnrollmentStatus = {};
        response.data.data.forEach(student => {
          initialEnrollmentStatus[student.id] = student.enrollmentStatus || 'Currently Enrolled';
        });
        setEnrollmentStatus(initialEnrollmentStatus);
     } catch (error) {
        console.error("Error fetching students:", error);
        setToast({
          show: true,
          message: 'Failed to load students',
          variant: 'danger'
        });
      } finally {
        setIsLoadingStudents(false);
      }
    };


    fetchStudents();
  }, [currentStudentsPage, searchQuery, sortCriteria, sortOrder]);

  useEffect(() => {
    if (showRecords) {
      fetchRecords();
    }
  }, [showRecords, currentRecordsPage, recordsSearchQuery, sortCriteria, sortOrder]);

  const fetchRecords = async () => {
        setIsLoadingRecords(true);
    try {
      const response = await attendanceAPI.getRecords({
        page: currentRecordsPage,
        limit: recordsPerPage,
        search: recordsSearchQuery,
        sortBy: sortCriteria,
        sortOrder: sortOrder
      });
      setAttendanceRecords(response.data.data);
      setTotalRecords(response.data.total);
   } catch (error) {
      console.error("Error fetching attendance records:", error);
      setToast({
        show: true,
        message: 'Failed to load attendance records',
        variant: 'danger'
      });
    } finally {
      setIsLoadingRecords(false);
    }
  };

  useEffect(() => {
    if (students.length > 0) {
      const initialEnrollmentStatus = {};
      students.forEach(student => {
        initialEnrollmentStatus[student.id] = student.enrollmentStatus || 'Currently Enrolled';
      });
      setEnrollmentStatus(initialEnrollmentStatus);
    }
  }, [students]);

  // auto-dismiss toast after 3 seconds
useEffect(() => {
  if (toast.show) {
    const timer = setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 3000);
    return () => clearTimeout(timer); // cleanup if toast changes before 3s
  }
}, [toast.show]);

  const handleEnrollmentChange = (id, value) => {
    setEnrollmentStatus(prev => ({ ...prev, [id]: value }));
    if (value !== 'Currently Enrolled') {
      const updatedAttendance = { ...attendance };
      delete updatedAttendance[id];
      setAttendance(updatedAttendance);
    }
  };

  const handleAttendanceChange = (id, value) => {
    setAttendance({ ...attendance, [id]: value });
  };

  const submitAttendance = async () => {
    if (!date) {
      setToast({
        show: true,
        message: 'Please select a date',
        variant: 'danger'
      });
      return;
    }
  
    const recordsToSubmit = students
      .filter(student => {
        const hasAttendanceSelected = attendance[student.id] !== undefined;
        const hasEnrollmentChange = enrollmentStatus[student.id] !== undefined && 
                                  enrollmentStatus[student.id] !== student.enrollmentStatus &&
                                  enrollmentStatus[student.id] !== 'Currently Enrolled';
        
        return hasAttendanceSelected || hasEnrollmentChange;
      })
      .map(student => ({
        date,
        id: student.id,
        studentName: student.name,
        class: student.class,
        registrationDate: student.registrationDate,
        attendanceStatus: enrollmentStatus[student.id] === 'Currently Enrolled' || 
                         (enrollmentStatus[student.id] === undefined && 
                          student.enrollmentStatus === 'Currently Enrolled')
          ? attendance[student.id]
          : 'N/A',
        enrollmentStatus: enrollmentStatus[student.id] || student.enrollmentStatus || 'Currently Enrolled'
      }));
  
    if (recordsToSubmit.length === 0) {
      setToast({
        show: true,
        message: 'No changes to submit',
        variant: 'danger'
      });
      return;
    }
  
    setIsSubmitting(true);
  
    try {
      await attendanceAPI.submitRecords(recordsToSubmit);
      
      setToast({
        show: true,
        message: `Successfully updated ${recordsToSubmit.length} record(s)`,
        variant: 'success'
      });
      
      const updatedAttendance = {...attendance};
      recordsToSubmit.forEach(record => {
        delete updatedAttendance[record.id];
      });
      setAttendance(updatedAttendance);
  
      setStudents(prevStudents => 
        prevStudents.map(student => {
          const submittedRecord = recordsToSubmit.find(r => r.id === student.id);
          if (submittedRecord && submittedRecord.enrollmentStatus !== student.enrollmentStatus) {
            return {
              ...student,
              enrollmentStatus: submittedRecord.enrollmentStatus
            };
          }
          return student;
        })
      );
  
      if (showRecords) {
        await fetchRecords();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      'Failed to submit attendance';
      setToast({
        show: true,
        message: errorMsg,
        variant: 'danger'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleRecords = () => {
    setShowRecords(!showRecords);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', dateString);
      return 'Invalid Date';
    }
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${month}/${day}/${year}`;
  };

  const handleSelectRecord = (recordId) => {
    setSelectedRecords(prevSelectedRecords =>
      prevSelectedRecords.includes(recordId)
        ? prevSelectedRecords.filter(id => id !== recordId)
        : [...prevSelectedRecords, recordId]
    );
  };

  const handleSelectAllRecords = () => {
    if (selectedRecords.length === attendanceRecords.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(attendanceRecords.map(record => record.id));
    }
  };

  const handleDelete = async () => {
    try {
      const response = await attendanceAPI.deleteRecords(selectedRecords);
      setToast({
        show: true,
        message: response.data.message || 'Selected records deleted successfully.',
        variant: 'success'
      });
      setSelectedRecords([]);
      setShowModal(false);
      setCurrentRecordsPage(1);
      fetchRecords();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 
                      'Failed to delete records';
      setToast({
        show: true,
        message: errorMsg,
        variant: 'danger'
      });
    }
  };

  const handleSortChange = (criteria, order) => {
    setSortCriteria(criteria);
    setSortOrder(order);
    setCurrentStudentsPage(1);
    setCurrentRecordsPage(1);
  };

  const getAttendanceStats = (studentName) => {
    const studentRecords = attendanceRecords.filter(record => record.name === studentName);
    const stats = {
      Present: 0,
      Absent: 0,
      Late: 0,
      Excused: 0,
      Total: studentRecords.length
    };
    studentRecords.forEach(record => {
      if (record.attendanceStatus && stats.hasOwnProperty(record.attendanceStatus)) {
        stats[record.attendanceStatus]++;
      }
    });
    return stats;
  };

  const renderAttendancePopover = (studentName) => {
    const stats = getAttendanceStats(studentName);
    return (
      <Popover id={`att-popover-${studentName}`} className="att-custom-popover">
        <Popover.Header as="h3" className="att-popover-header">{studentName}'s Attendance</Popover.Header>
        <Popover.Body className="att-popover-body">
          <div className="att-stats-container">
            <p className="att-stat-total"><strong>Total Records:</strong> {stats.Total}</p>
            <div className="att-stats-grid">
              <div className="att-stat-item att-stat-present">
                <span className="att-stat-label">Present:</span>
                <span className="att-stat-value">{stats.Present} ({stats.Total > 0 ? Math.round((stats.Present/stats.Total)*100) : 0}%)</span>
              </div>
              <div className="att-stat-item att-stat-absent">
                <span className="att-stat-label">Absent:</span>
                <span className="att-stat-value">{stats.Absent} ({stats.Total > 0 ? Math.round((stats.Absent/stats.Total)*100) : 0}%)</span>
              </div>
              <div className="att-stat-item att-stat-late">
                <span className="att-stat-label">Late:</span>
                <span className="att-stat-value">{stats.Late} ({stats.Total > 0 ? Math.round((stats.Late/stats.Total)*100) : 0}%)</span>
              </div>
              <div className="att-stat-item att-stat-excused">
                <span className="att-stat-label">Excused:</span>
                <span className="att-stat-value">{stats.Excused} ({stats.Total > 0 ? Math.round((stats.Excused/stats.Total)*100) : 0}%)</span>
              </div>
            </div>
          </div>
        </Popover.Body>
      </Popover>
    );
  };

  const StudentsPagination = () => {
    const totalPages = Math.ceil(totalStudents / studentsPerPage);
    return (
      <div className="att-pagination-container">
        <div className="att-pagination-info">
          {students.length === 0 ? 'No records found' : 
            `${Math.min(currentStudentsPage * studentsPerPage, totalStudents)} of ${totalStudents} records`}
        </div>
        <nav>
          <ul className="att-pagination">
            <li className={`att-page-item ${currentStudentsPage === 1 ? 'att-disabled' : ''}`}>
              <button className="att-page-link" onClick={() => setCurrentStudentsPage(1)} disabled={currentStudentsPage === 1}>
                First
              </button>
            </li>
            <li className={`att-page-item ${currentStudentsPage === 1 ? 'att-disabled' : ''}`}>
              <button className="att-page-link" onClick={() => setCurrentStudentsPage(currentStudentsPage - 1)} disabled={currentStudentsPage === 1}>
                Previous
              </button>
            </li>
            {[...Array(totalPages).keys()].map(number => (
              <li key={number + 1} className={`att-page-item ${currentStudentsPage === number + 1 ? 'att-active' : ''}`}>
                <button className="att-page-link" onClick={() => setCurrentStudentsPage(number + 1)}>
                  {number + 1}
                </button>
              </li>
            ))}
            <li className={`att-page-item ${currentStudentsPage === totalPages ? 'att-disabled' : ''}`}>
              <button className="att-page-link" onClick={() => setCurrentStudentsPage(currentStudentsPage + 1)} disabled={currentStudentsPage === totalPages}>
                Next
              </button>
            </li>
            <li className={`att-page-item ${currentStudentsPage === totalPages ? 'att-disabled' : ''}`}>
              <button className="att-page-link" onClick={() => setCurrentRecordsPage(totalPages)} disabled={currentStudentsPage === totalPages}>
                Last
              </button>
            </li>
          </ul>
        </nav>
      </div>
    );
  };

  const RecordsPagination = () => {
    const totalPages = Math.ceil(totalRecords / recordsPerPage);
    return (
      <div className="att-records-pagination">
        <div className="att-pagination-info">
          {attendanceRecords.length === 0 ? 'No records found' : 
            `${Math.min(currentRecordsPage * recordsPerPage, totalRecords)} of ${totalRecords} records`}
        </div>
        <nav>
          <ul className="att-pagination">
            <li className={`att-page-item ${currentRecordsPage === 1 ? 'att-disabled' : ''}`}>
              <button className="att-page-link" onClick={() => setCurrentRecordsPage(1)} disabled={currentRecordsPage === 1}>
                First
              </button>
            </li>
            <li className={`att-page-item ${currentRecordsPage === 1 ? 'att-disabled' : ''}`}>
              <button className="att-page-link" onClick={() => setCurrentRecordsPage(currentRecordsPage - 1)} disabled={currentRecordsPage === 1}>
                Previous
              </button>
            </li>
            {[...Array(totalPages).keys()].map(number => (
              <li key={number + 1} className={`att-page-item ${currentRecordsPage === number + 1 ? 'att-active' : ''}`}>
                <button className="att-page-link" onClick={() => setCurrentRecordsPage(number + 1)}>
                  {number + 1}
                </button>
              </li>
            ))}
            <li className={`att-page-item ${currentRecordsPage === totalPages ? 'att-disabled' : ''}`}>
              <button className="att-page-link" onClick={() => setCurrentRecordsPage(currentRecordsPage + 1)} disabled={currentRecordsPage === totalPages}>
                Next
              </button>
            </li>
            <li className={`att-page-item ${currentRecordsPage === totalPages ? 'att-disabled' : ''}`}>
              <button className="att-page-link" onClick={() => setCurrentRecordsPage(totalPages)} disabled={currentRecordsPage === totalPages}>
                Last
              </button>
            </li>
          </ul>
        </nav>
      </div>
    );
  };

  const ExportDropdown = () => {
    const exportToExcel = () => {
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';
      const exportData = attendanceRecords.map(record => ({
        Date: formatDateTime(record.date),
        'Student Name': record.name,
        Class: record.class,
        'Attendance Status': record.attendanceStatus,
        'Enrollment Status': record.enrollmentStatus,
        'Registration Date': formatDateTime(record.registrationDate)
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: fileType });
      const fileName = 'AttendanceRecords' + fileExtension;
      saveAs(data, fileName);
    };

    const exportToCSV = () => {
      const csvData = attendanceRecords.map(record => ({
        Date: formatDateTime(record.date),
        'Student Name': record.name,
        Class: record.class,
        'Attendance Status': record.attendanceStatus,
        'Enrollment Status': record.enrollmentStatus,
        'Registration Date': formatDateTime(record.registrationDate)
      }));

      const csv = convertToCSV(csvData);
      const fileName = 'AttendanceRecords.csv';
      downloadCSV(csv, fileName);
    };

    const convertToCSV = (objArray) => {
      const array = typeof objArray != 'object' ? JSON.parse(objArray) : objArray;
      let str = '';
      const headers = Object.keys(array[0]);
      str += headers.join(',') + '\r\n';
      for (let i = 0; i < array.length; i++) {
        let line = '';
        headers.forEach(header => {
          if (line !== '') line += ',';
          line += array[i][header];
        });
        str += line + '\r\n';
      }
      return str;
    };

    const downloadCSV = (csv, filename) => {
      const csvFile = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      if (navigator.msSaveBlob) {
        navigator.msSaveBlob(csvFile, filename);
      } else {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(csvFile);
        link.style = 'visibility:hidden';
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    };

    const exportToPDF = () => {
      const doc = new jsPDF();
      const columns = [
        'Date', 
        'Student Name', 
        'Class', 
        'Attendance Status', 
        'Enrollment Status',
        'Registration Date'
      ];
      const data = attendanceRecords.map(record => [
        formatDateTime(record.date),
        record.name,
        record.class,
        record.attendanceStatus,
        record.enrollmentStatus,
        formatDateTime(record.registrationDate)
      ]);

      let yPos = 10;
      doc.text(10, yPos, 'Attendance Data');
      yPos += 10;

      doc.autoTable({
        startY: yPos,
        head: [columns],
        body: data,
      });

      doc.save('attendance.pdf');
    };

    return (
      <Dropdown className="att-export-dropdown">
        <Dropdown.Toggle variant="primary" id="att-export-dropdown" className="att-export-toggle">
          Export
        </Dropdown.Toggle>
        <Dropdown.Menu className="att-export-menu">
          <Dropdown.Item onClick={exportToExcel} className="att-export-item">Export to Excel</Dropdown.Item>
          <Dropdown.Item onClick={exportToCSV} className="att-export-item">Export to CSV</Dropdown.Item>
          <Dropdown.Item onClick={exportToPDF} className="att-export-item">Export to PDF</Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const renderAttendanceView = () => {
    switch (userRole) {
      case 'student':
        return <StudentAttendance />;
      case 'staff':
      case 'admin':
        return <TeacherQRGenerator />;
      default:
        return null;
    }
  };

  return (
    <>
        <div className="att-page">

   <div className="row my-4">
  <div className="col-12">
    <h3>
      Attendance
      <img className="animated-icons" src={AttendanceIcon} alt="Attendance icon" />
    </h3>
  </div>
</div>

      {userRole === 'student' ? (
        <div className="att-student-view">
          <StudentAttendance />
        </div>
      ) : (
        <div className="att-admin-container">
          <div className="att-admin-content">
            {renderAttendanceView()}

            <h4 className="att-section-title">Record Attendance</h4>

            <div className="att-search-wrapper">
              <Search className="att-search-icon" />
              <input 
                value={searchQuery}
                type="text" 
                className="att-search-input" 
                placeholder="Search by name or class..." 
                onChange={(e) => setSearchQuery(e.target.value)} 
              />
            </div>

            <div className="att-form-card">
              <Dropdown className="att-sort-dropdown">
                <Dropdown.Toggle variant="warning" id="att-sort-dropdown" className="att-sort-toggle">
                  Sort by: {sortCriteria} {sortOrder}
                </Dropdown.Toggle>
                <Dropdown.Menu className="att-sort-menu">
                  <Dropdown.Item onClick={() => handleSortChange('name', 'asc')} className="att-sort-item">Name-Ascending</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSortChange('name', 'desc')} className="att-sort-item">Name-Descending</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSortChange('class', 'asc')} className="att-sort-item">Class-Ascending</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSortChange('class', 'desc')} className="att-sort-item">Class-Descending</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              <div className="att-date-field">
                <input
                  type="date"
                  className="att-date-input"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
                <label className="att-date-label">Date:</label>
              </div>
            </div>

            <div className="att-table-container">
              <div className="att-table-wrapper">
                <table className="att-table">
                  <thead className="att-table-header">
                    <tr>
                      <th>Name</th>
                      <th>Class</th>
                      <th>Attendance Status</th>
                      <th>Enrollment Status</th>
                      <th>Status</th>
                      <th>Registration Date</th>
                    </tr>
                  </thead>
                 <tbody className="att-table-body">
                    {isLoadingStudents ? (
                      <tr>
                        <td colSpan="6" className="att-loading-state">
                          <div className="att-loader-wrap">
                            <div className="att-loader-ring" />
                            <span className="att-loader-text">Loading Students</span>
                          </div>
                        </td>
                      </tr>
                    ) : students.length > 0 ? (
                      students.map(student => {
                        const hasAttendance = !!attendance[student.id];
                        const isEnrolled = enrollmentStatus[student.id] === 'Currently Enrolled';
                        const enrollmentClass = 
                          (enrollmentStatus[student.id] || student.enrollmentStatus) === 'Expelled' ? 'att-row-expelled' :
                          (enrollmentStatus[student.id] || student.enrollmentStatus) === 'Withdrawn' ? 'att-row-withdrawn' :
                          (enrollmentStatus[student.id] || student.enrollmentStatus) === 'Passed Out' ? 'att-row-passed' : '';
                        return (
                          <tr key={student.id} className={enrollmentClass}>
                            <td className="att-cell-name">{student.name}</td>
                            <td className="att-cell-class">{student.class}</td>
                            <td className="att-cell-attendance">
                              <select
                                className="att-select"
                                onChange={(e) => handleAttendanceChange(student.id, e.target.value)}
                                value={attendance[student.id] || ''}
                                disabled={!isEnrolled}
                              >
                                <option value="">Select Status</option>
                                <option value="Present">Present</option>
                                <option value="Absent">Absent</option>
                                <option value="Late">Late</option>
                                <option value="Excused">Excused</option>
                              </select>
                            </td>
                            <td className="att-cell-enrollment">
                              <select
                                className={`att-select ${enrollmentClass}`}
                                value={enrollmentStatus[student.id] || student.enrollmentStatus || 'Currently Enrolled'}
                                onChange={(e) => handleEnrollmentChange(student.id, e.target.value)}
                              >
                                <option value="Currently Enrolled" style={{ display: 'none' }}>Update</option>
                                <option value="Currently Enrolled">Currently Enrolled</option>
                                <option value="Passed Out">Passed Out</option>
                                <option value="Expelled">Expelled</option>
                                <option value="Withdrawn">Withdrawn</option>
                              </select>
                            </td>
                            <td className="att-cell-status">
                              {isEnrolled ? (
                                hasAttendance ? (
                                  <span className="att-badge att-badge-success">Will be submitted</span>
                                ) : (
                                  <span className="att-badge att-badge-secondary">No changes</span>
                                )
                              ) : (
                                enrollmentStatus[student.id] !== student.enrollmentStatus ? (
                                  <span className="att-badge att-badge-success">Will be submitted</span>
                                ) : (
                                  <span className="att-badge att-badge-secondary">No changes</span>
                                )
                              )}
                            </td>
                            <td className="att-cell-date">{formatDateTime(student.registrationDate)}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="6" className="att-empty-state">No results found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="att-pagination-wrapper">
              <StudentsPagination />
            </div>

            <div className="att-action-bar">
              <button
                className="att-btn att-btn-success"
                onClick={submitAttendance}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="att-spinner" role="status" aria-hidden="true"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit Attendance'
                )}
              </button>
              <button
                className="att-btn att-btn-secondary"
                onClick={toggleRecords}
              >
                {showRecords ? 'Hide Records' : 'View Records'}
              </button>
              <ExportDropdown />
            </div>

            {showRecords && (
              <div className="att-records-section">
                <h4 className="att-section-title att-records-title">Submitted Attendance Records</h4>
                <div className="att-records-toolbar">
                  <button
                    className="att-btn att-btn-danger"
                    onClick={() => setShowModal(true)}
                    disabled={selectedRecords.length === 0}
                  >
                    Delete Selected Records
                  </button>
                  <div className="att-selected-count">
                    Selected: {selectedRecords.length} record{selectedRecords.length !== 1 ? 's' : ''}
                  </div>
                </div>

                <div className="att-records-search">
                  <div className="att-search-wrapper att-search-small">
                    <Search className="att-search-icon" />
                    <input
                      type="text"
                      className="att-search-input"
                      placeholder="Search records..."
                      value={recordsSearchQuery}
                      onChange={(e) => setRecordsSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                <Dropdown className="att-sort-dropdown att-sort-records">
                  <Dropdown.Toggle variant="warning" id="att-sort-records" className="att-sort-toggle">
                    Sort by: {sortCriteria} {sortOrder}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="att-sort-menu">
                    <Dropdown.Item onClick={() => handleSortChange('name', 'asc')} className="att-sort-item">Name-Ascending</Dropdown.Item>
                    <Dropdown.Item onClick={() => handleSortChange('name', 'desc')} className="att-sort-item">Name-Descending</Dropdown.Item>
                    <Dropdown.Item onClick={() => handleSortChange('class', 'asc')} className="att-sort-item">Class-Ascending</Dropdown.Item>
                    <Dropdown.Item onClick={() => handleSortChange('class', 'desc')} className="att-sort-item">Class-Descending</Dropdown.Item>
                    <Dropdown.Item onClick={() => handleSortChange('date', 'asc')} className="att-sort-item">Date-Ascending</Dropdown.Item>
                    <Dropdown.Item onClick={() => handleSortChange('date', 'desc')} className="att-sort-item">Date-Descending</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>

                <div className="att-records-table-container">
                  <div className="att-table-wrapper">
                    <table className="att-table att-records-table">
                      <thead className="att-table-header">
                        <tr>
                          <th className="att-checkbox-cell">
                            <input
                              type="checkbox"
                              onChange={handleSelectAllRecords}
                              checked={selectedRecords.length === attendanceRecords.length && selectedRecords.length > 0}
                              className="att-checkbox"
                            />
                          </th>
                          <th>Date</th>
                          <th>Name</th>
                          <th>Class</th>
                          <th>Attendance Status</th>
                          <th>Enrollment Status</th>
                          <th>Registration Date</th>
                        </tr>
                      </thead>
                      <tbody className="att-table-body">
                        {isLoadingRecords ? (
                          <tr>
                            <td colSpan="7" className="att-loading-state">
                              <div className="att-loader-wrap">
                                <div className="att-loader-ring" />
                                <span className="att-loader-text">Loading Records</span>
                              </div>
                            </td>
                          </tr>
                        ) : attendanceRecords.length > 0 ? (
                          attendanceRecords.map(record => {
                            const rowClass = 
                              record.enrollmentStatus === 'Expelled' ? 'att-row-expelled' :
                              record.enrollmentStatus === 'Withdrawn' ? 'att-row-withdrawn' :
                              record.enrollmentStatus === 'Passed Out' ? 'att-row-passed' : '';
                            return (
                              <tr key={`${record.id}-${record.date}`} className={rowClass}>
                                <td className="att-checkbox-cell">
                                  <input
                                    type="checkbox"
                                    onChange={() => handleSelectRecord(record.id)}
                                    checked={selectedRecords.includes(record.id)}
                                    className="att-checkbox"
                                  />
                                </td>
                                <td className="att-cell-date">{formatDateTime(record.date)}</td>
                                <td className="att-cell-name">
                                  <OverlayTrigger
                                    trigger={['hover', 'focus']}
                                    placement="right"
                                    overlay={renderAttendancePopover(record.name)}
                                    delay={{ show: 250, hide: 400 }}
                                  >
                                    <span className="att-name-hover">
                                      {record.name}
                                    </span>
                                  </OverlayTrigger>
                                </td>
                                <td className="att-cell-class">{record.class}</td>
                                <td className="att-cell-attendance-status">{record.attendanceStatus}</td>
                                <td className="att-cell-enrollment-status">{record.enrollmentStatus}</td>
                                <td className="att-cell-date">{formatDateTime(record.registrationDate)}</td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="7" className="att-empty-state">No results found.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <RecordsPagination />
              </div>
            )}
          </div>
        </div>
      )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} className="att-modal">
        <Modal.Header closeButton className="att-modal-header">
          <Modal.Title className="att-modal-title">Delete Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body className="att-modal-body">Are you sure you want to delete the selected records?</Modal.Body>
        <Modal.Footer className="att-modal-footer">
          <Button variant="secondary" onClick={() => setShowModal(false)} className="att-modal-btn att-modal-btn-secondary">
            Close
          </Button>
          <Button variant="danger" onClick={handleDelete} className="att-modal-btn att-modal-btn-danger">
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <div className={`att-toast att-toast-${toast.variant} ${toast.show ? 'att-toast-show' : ''}`}>
        <div className="att-toast-header">
          <strong className="att-toast-title">
            {toast.variant === 'success' ? 'Success' : 'Error'}
          </strong>
          <button className="att-toast-close" onClick={() => setToast({...toast, show: false})}>×</button>
        </div>
        <div className="att-toast-body">
          {toast.variant === 'success' ? (
            <CheckCircleFill className="att-toast-icon" />
          ) : (
            <ExclamationTriangleFill className="att-toast-icon" />
          )}
          {toast.message}
        </div>
      </div>

       {/* Guide FAB — renders for non-students only */}
    {userRole !== 'student' && <AttendanceGuideModal />}
    </>
  );
}

export default Attendance;